"use client";

import { IS_SAFARI } from "@/lib/utils/device";
import { classed, clsx } from "@frend-digital/ui";
import { useHydrated, useMediaQuery } from "@frend-digital/ui/hooks";
import {
  createContext,
  use,
  useContext,
  useId,
  useRef,
  useState,
  type ComponentProps,
  type RefObject,
} from "react";
import { Anchor } from "../Anchor";
import { Slot } from "../Slot";
import styles from "./popover.module.css";

interface PopoverContextValue {
  popoverId: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  ref: RefObject<HTMLDivElement | null>;
}

const PopoverContext = createContext<PopoverContextValue>(null!);

export const PopoverRoot = ({ children }: { children: React.ReactNode }) => {
  const id = useId();

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null!);

  return (
    <PopoverContext.Provider value={{ popoverId: id, open, setOpen, ref }}>
      {children}
    </PopoverContext.Provider>
  );
};

export const usePopover = () => {
  const context = useContext(PopoverContext);
  if (!context) {
    throw new Error("usePopover must be used within a PopoverRoot");
  }
  return context;
};
export const PopoverContent = ({
  children,
  className,
  asChild,
  relativeAnchor,
  ...props
}: ComponentProps<"div"> & {
  asChild?: boolean;
  relativeAnchor?: boolean;
}) => {
  const { popoverId, open, setOpen, ref: popoverRef } = use(PopoverContext);

  const Component = asChild ? Slot : "div";

  const isMobile = useMediaQuery("(max-width: 1024px)");

  const hydrated = useHydrated();

  if (hydrated && (IS_SAFARI || isMobile)) {
    return (
      <Component
        ref={(el: HTMLDivElement | null) => {
          popoverRef.current = el;
          if (el) {
            const controller = new AbortController();
            document.body.addEventListener(
              "click",
              (e) => {
                const target = e.target as HTMLElement;
                if (e.target === el || el.contains(target)) {
                  // WE ARE INSIDE
                  setOpen(true);
                } else {
                  setOpen(false);
                }
              },
              { signal: controller.signal },
            );

            return () => {
              controller.abort();
            };
          }
        }}
        {...props}
        className={clsx(
          styles.Popover,
          className,
          relativeAnchor && styles.useRelativeAnchor,
        )}
        data-popover-open={open ? "open" : "closed"}
      >
        {children}
      </Component>
    );
  }

  return (
    <Component
      ref={popoverRef}
      popover="auto"
      {...props}
      id={popoverId}
      className={clsx(styles.Popover, className)}
    >
      {children}
    </Component>
  );
};

export const PopoverTrigger = ({
  asChild,
  children,
  ...props
}: ComponentProps<"button"> & {
  asChild?: boolean;
}) => {
  const Component = asChild ? Slot : "button";
  const { popoverId, setOpen, ref } = use(PopoverContext);

  return (
    <Anchor asChild containerSelector={`#${popoverId}`} containerRef={ref}>
      <Component
        {...props}
        onClick={(e: React.MouseEvent) => {
          setOpen(true);
          if (props.onClick) {
            props.onClick(e as unknown as React.MouseEvent<HTMLButtonElement>);
          }
        }}
        popoverTarget={popoverId}
        popoverTargetAction="toggle"
      >
        {children}
      </Component>
    </Anchor>
  );
};

export const PopoverClose = ({
  asChild,
  children,
  ...props
}: ComponentProps<"button"> & {
  asChild?: boolean;
}) => {
  const Component = asChild ? Slot : "button";
  const { popoverId, setOpen } = use(PopoverContext);

  return (
    <Component
      {...props}
      onClick={() => setOpen(false)}
      popoverTarget={popoverId}
      popoverTargetAction="hide"
    >
      {children}
    </Component>
  );
};

export const PopoverCloseIcon = classed(PopoverClose, styles.Close);

export const PopoverFooter = classed.footer(styles.Footer);
